import type { CSSProperties } from 'react';

/**
 * Shared chrome for the full-screen editor.
 *
 * Two things live here: the row heights the track gutter and the tracks must
 * agree on (they are separate columns — a disagreement misaligns every lane),
 * and the class strings that keep the bars in one visual language. The language
 * is the app's own: hard 1px borders, mono micro-caps, and the accent reserved
 * for exactly three things — the render button, the playhead and selection.
 */

/**
 * `narration` is the audio band plus the caption band beneath it: the narrator
 * track and the captions it speaks belong to one lane, and stacking them keeps
 * the two readable against each other without a second gutter row.
 */
export const ROW_HEIGHTS = { ruler: 22, video: 64, audio: 40, narration: 40 + 32 } as const;
export const GUTTER_WIDTH = 132;
/** Height of every track row stacked, i.e. the timeline body's natural height. */
export const TRACKS_HEIGHT =
  ROW_HEIGHTS.ruler + ROW_HEIGHTS.video + ROW_HEIGHTS.audio + ROW_HEIGHTS.narration;

export const MICRO_LABEL = 'text-[10px] font-mono uppercase tracking-widest';
export const MUTED_LABEL = `${MICRO_LABEL} text-[var(--app-text-muted)]`;

export const TOOL_BUTTON =
  `${MICRO_LABEL} whitespace-nowrap border border-[var(--border-subtle)] px-2 py-1 text-[var(--app-text-muted)] ` +
  'transition-colors hover:border-white/40 hover:text-[var(--app-text)] ' +
  'disabled:opacity-40 disabled:cursor-not-allowed';
export const TOOL_BUTTON_ON =
  `${MICRO_LABEL} whitespace-nowrap border border-[var(--accent-strong)] px-2 py-1 text-[var(--app-text)]`;

export const toolToggle = (on: boolean): string => (on ? TOOL_BUTTON_ON : TOOL_BUTTON);

/**
 * The preview's field — the only textured surface in the app. It exists to say
 * "this is canvas, not chrome", which is what stops the editor reading as a
 * stack of widgets.
 */
export const DOT_GRID: CSSProperties = {
  backgroundColor: 'var(--app-bg-solid)',
  backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.10) 1px, transparent 1px)',
  backgroundSize: '16px 16px',
};

/**
 * The timeline's second grid, drawn as two repeating gradients rather than a
 * tick per second — zero DOM nodes at any zoom.
 */
export const trackGrid = (pxPerSecond: number): CSSProperties => ({
  backgroundImage: [
    `repeating-linear-gradient(90deg, rgba(255,255,255,0.09) 0 1px, transparent 1px ${pxPerSecond * 5}px)`,
    `repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px ${pxPerSecond}px)`,
  ].join(','),
});
